import React, { useEffect, useState } from 'react'
// @ts-ignore no types
import {
  Modal,
  Text,
  constants,
  hooks,
  useApplicationContext,
} from '@adc/ui-adc'
import { useListingContext } from 'common/context/listing'
import {
  PATH_CONFIG,
  getPurchasePaths,
  type PurchasePath,
} from 'common/utils/listingPaths'
import analytics from 'common/services/analytics'
import classnames from 'classnames/bind'
import PathOptionList from '../pathOptionList'
import adcLogo from '../../../images/adc-logo.svg'
// @ts-ignore no types
import cls from './styles.scss'

const cx = classnames.bind(cls)
const { ColorNames } = constants

const NUDGE_KEY = 'dual_track_path'
const NUDGE_DELAY_MS = 100

// Whether the path modal is currently open. Read by other PDP prompts (e.g.
// creditBidLoginModal) so they don't stack on top of it. Mutated only in a
// client-side effect, so it stays false during SSR.
let pathModalOpen = false
export const isPathModalOpen = () => pathModalOpen

// Module scope so it survives the listing-swap remount — a `useRef` is
// destroyed with the instance, which is how the modal got counted twice.
let viewTracked = false
export const __resetViewTracking = () => {
  viewTracked = false
}

export interface ChoosePathModalProps {
  // Controlled by an ancestor above PropertyWithSegment: selecting a path can
  // flip the current listing between online and live auctions, which changes
  // the PropertyWithSegment component identity and remounts this subtree.
  // Owning the interaction state higher up keeps the modal dismissed across
  // those remounts (show once per visit).
  hasInteracted: boolean
  onInteract: () => void
}

/**
 * Choose-a-path landing modal for the unified PDP. Shown when a property has
 * more than one purchase path (and the flag is on), prompting the buyer to pick
 * one. Selecting an option switches the current listing and closes the modal.
 */
export const ChoosePathModalComponent = ({
  hasInteracted,
  onInteract,
}: ChoosePathModalProps) => {
  const { featureFlags } = useApplicationContext()
  const { activeListings, listingActions } = useListingContext()
  const { data, actions } = hooks.useWebApplicationNudges()

  const unifiedPdpEnabled = featureFlags?.['fe.resi.enable-unified-pdp']
  const paths = getPurchasePaths(activeListings)
  const isEligible =
    Boolean(unifiedPdpEnabled) &&
    paths.length >= 2 &&
    !hasInteracted &&
    data.active.has(NUDGE_KEY)

  // Nudge data is read asynchronously, so wait before acting on it. Open state
  // is latched because recording the nudge drops the key from `active`.
  const [isVisible, setIsVisible] = useState(false)
  useEffect(() => {
    if (!isEligible) return undefined
    const timer = setTimeout(() => {
      actions.nudge(NUDGE_KEY)
      setIsVisible(true)
    }, NUDGE_DELAY_MS)
    return () => clearTimeout(timer)
  }, [isEligible, actions])

  const isOpen = isVisible && !hasInteracted

  // Publish open-state at module scope so timed prompts (e.g. the credit-bid
  // login nudge) can defer to this modal without a shared context.
  useEffect(() => {
    pathModalOpen = isOpen
    return () => {
      pathModalOpen = false
    }
  }, [isOpen])

  useEffect(() => {
    // Only clear once the modal is genuinely finished, not while `isVisible`
    // is still warming up after a remount — that would re-count the view.
    if (!isEligible && !isOpen) {
      viewTracked = false
      return
    }
    if (!isOpen || viewTracked) return
    viewTracked = true
    analytics.dualTrack.modalViewed()
  }, [isOpen, isEligible])

  if (!isOpen) return null

  const choose = (path: PurchasePath) => {
    analytics.dualTrack.pathSelected({
      path: PATH_CONFIG[path.type].analyticsPath,
      source: 'modal',
    })
    listingActions?.setCurrentListingId(path.listing.listing_id)
    onInteract()
  }

  return (
    <Modal
      open
      size='tiny'
      fullScreen='never'
      slideUp='mobileOnly'
      closeIcon={false}
      // `open` is hardcoded, so the prop always wins — the dimmer click only
      // notifies via `onClose`, it can't actually close the modal.
      closeOnDimmerClick
      onClose={analytics.dualTrack.modalOutsideClick}
      closeOnEscape={false}
      // The slide-up variant isn't held open by `open`; it needs this.
      dismissible={false}
      className={cx('choose-path-modal')}
      elmId='choose-a-path-modal'
      title={
        <span className={cx('modal-title')}>
          <img src={adcLogo} alt='' className={cx('modal-logo')} />
          <Text as='h2' size='huge' weight='heavy' color={ColorNames.GrayDark}>
            Choose a way to buy
          </Text>
        </span>
      }
    >
      <Modal.Content>
        <div className={cx('choose-path')}>
          <PathOptionList
            paths={paths}
            elmIdPrefix='choose-a-path-modal'
            onSelect={choose}
            variant='modal'
          />
        </div>
      </Modal.Content>
    </Modal>
  )
}

export default ChoosePathModalComponent
